import { useContext, useEffect, useState } from "react";
import {
  TextField,
  Autocomplete,
  Typography,
  InputAdornment,
  Box,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment, { Moment } from "moment";
import { CacheContext } from "../../Context/cacheContext";
import { Vocabulary } from "../../Utils/Vocabulary";
import { isMobile } from "../../Utils/utils";
import { NumericFormatCustom } from "../../Utils/NumericFormatComponent";

export type BuyBackFieldsData = {
  brand: { id: number; nume: string } | null;
  model: { id: number; nume: string; brand_id: number } | null;
  vin: string;
  engineCapacity: string;
  yearOfManufacture: string;
  licensePlateNumber: string;
  itpExpirationDate: Moment | null;
  mileage: string;
  vehicleIdentityCardNumber: string;
  priceRon: string;
};

type BuyBackFieldsProps = {
  data: BuyBackFieldsData;
  onChange: (field: keyof BuyBackFieldsData, value: unknown) => void;
  errors?: Partial<Record<keyof BuyBackFieldsData, string>>;
};

export default function BuyBackFields({
  data,
  onChange,
  errors = {},
}: BuyBackFieldsProps) {
  const cacheContext = useContext(CacheContext);
  const [filteredModels, setFilteredModels] = useState<
    { id: number; nume: string; brand_id: number }[]
  >([]);

  useEffect(() => {
    if (data.brand?.id) {
      const models =
        cacheContext?.cache?.models?.filter(
          (m: any) => m.brand_id === data.brand!.id
        ) || [];
      setFilteredModels(models);
    } else {
      setFilteredModels([]);
    }
  }, [data.brand, cacheContext?.cache?.models]);

  const sectionStyle = {
    backgroundColor: "#fafafa",
    borderRadius: 2,
    padding: 2,
    marginTop: 2,
  };

  const labelStyle = {
    fontSize: 13,
    fontWeight: 600,
    color: "#666",
    marginBottom: 1.5,
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Vehicle Identity Section */}
      <Box sx={sectionStyle}>
        <Typography sx={labelStyle}>{Vocabulary.buyBackVehicleData}</Typography>

        {/* Brand & Model */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile() ? "column" : "row",
            gap: 2,
            mb: 2,
          }}
        >
          <Autocomplete
            freeSolo={false}
            size="small"
            fullWidth
            disablePortal
            value={data.brand}
            onChange={(_, value) => {
              onChange("brand", value);
              onChange("model", null);
            }}
            getOptionLabel={(option) => option?.nume || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            options={cacheContext?.cache?.brands || []}
            renderInput={(params) => (
              <TextField
                {...params}
                label={Vocabulary.brand}
                error={!!errors.brand}
                helperText={errors.brand}
                required
              />
            )}
          />
          <Autocomplete
            freeSolo={false}
            size="small"
            fullWidth
            disablePortal
            value={data.model}
            onChange={(_, value) => onChange("model", value)}
            getOptionLabel={(option) => option?.nume || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            options={filteredModels}
            disabled={!data.brand}
            renderInput={(params) => (
              <TextField
                {...params}
                label={Vocabulary.model}
                error={!!errors.model}
                helperText={errors.model}
                required
              />
            )}
          />
        </Box>

        {/* VIN & Engine Capacity */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile() ? "column" : "row",
            gap: 2,
            mb: 2,
          }}
        >
          <TextField
            size="small"
            fullWidth
            label={Vocabulary.vin}
            value={data.vin}
            onChange={(e) => onChange("vin", e.target.value.toUpperCase())}
            error={!!errors.vin}
            helperText={errors.vin}
            inputProps={{ maxLength: 17 }}
          />
          <TextField
            size="small"
            fullWidth
            label={Vocabulary.capacity}
            value={data.engineCapacity}
            onChange={(e) => onChange("engineCapacity", e.target.value)}
            error={!!errors.engineCapacity}
            helperText={errors.engineCapacity}
            InputProps={{
              inputComponent: NumericFormatCustom as any,
              endAdornment: <InputAdornment position="end">cm³</InputAdornment>,
            }}
          />
        </Box>

        {/* Year & License Plate */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile() ? "column" : "row",
            gap: 2,
            mb: 2,
          }}
        >
          <TextField
            size="small"
            fullWidth
            label={Vocabulary.yearOfManufacture}
            value={data.yearOfManufacture}
            onChange={(e) => onChange("yearOfManufacture", e.target.value)}
            error={!!errors.yearOfManufacture}
            helperText={errors.yearOfManufacture}
          />
          <TextField
            size="small"
            fullWidth
            label={Vocabulary.licensePlateNumber}
            value={data.licensePlateNumber}
            onChange={(e) =>
              onChange("licensePlateNumber", e.target.value.toUpperCase())
            }
            error={!!errors.licensePlateNumber}
            helperText={errors.licensePlateNumber}
          />
        </Box>

        {/* ITP Expiry & Mileage */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile() ? "column" : "row",
            gap: 2,
            mb: 2,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label={Vocabulary.itpExpiration}
              value={
                data.itpExpirationDate
                  ? moment(data.itpExpirationDate)
                  : null
              }
              onChange={(date) => onChange("itpExpirationDate", date)}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: !!errors.itpExpirationDate,
                  helperText: errors.itpExpirationDate,
                  inputProps: { readOnly: true },
                },
              }}
            />
          </LocalizationProvider>
          <TextField
            size="small"
            fullWidth
            label={Vocabulary.mileage}
            value={data.mileage}
            onChange={(e) => onChange("mileage", e.target.value)}
            error={!!errors.mileage}
            helperText={errors.mileage}
            InputProps={{
              inputComponent: NumericFormatCustom as any,
              endAdornment: <InputAdornment position="end">km</InputAdornment>,
            }}
          />
        </Box>

        {/* CIV & Price */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile() ? "column" : "row",
            gap: 2,
          }}
        >
          <TextField
            size="small"
            fullWidth
            label={Vocabulary.vehicleIdentityCardNumber}
            value={data.vehicleIdentityCardNumber}
            onChange={(e) =>
              onChange("vehicleIdentityCardNumber", e.target.value)
            }
            error={!!errors.vehicleIdentityCardNumber}
            helperText={errors.vehicleIdentityCardNumber}
          />
          <TextField
            size="small"
            fullWidth
            label={Vocabulary.priceRon}
            value={data.priceRon}
            onChange={(e) => onChange("priceRon", e.target.value)}
            error={!!errors.priceRon}
            helperText={errors.priceRon}
            InputProps={{
              inputComponent: NumericFormatCustom as any,
              endAdornment: (
                <InputAdornment position="end">
                  {Vocabulary.currencyLei}
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
